import { createFileRoute, isNotFound, useRouteContext } from '@tanstack/react-router';
import { buttonVariants } from '@worldcoo/ui/components/button';
import * as z from 'zod';
import { RadioGroup, RadioGroupItem } from '@worldcoo/ui/components/radio-group';
import { useLingui } from '@lingui/react/macro';
import { errorMessages } from '@/lib/portal-messages';
import { cn } from '@worldcoo/ui/lib/utils';
import CurrencyInput from 'react-currency-input-field';
import { Title } from '@worldcoo/ui/components/form/title';
import { Paragraph } from '@worldcoo/ui/components/form/paragraph';
import { useAppForm } from '@/lib/use-app-form';
import { Label } from '@worldcoo/ui/components/label';
import { Animate } from '@worldcoo/ui/custom/animate';
import { usePersistedStore } from '@/lib/app-store';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { FieldInfo } from '@worldcoo/ui/components/form/shared';
import { localeToLanguage } from '@worldcoo/shared/core';
import { useState } from 'react';
import { HeartIcon } from './-components/assets/heart-icon';

export const Route = createFileRoute('/$portalId/$campaignId/donate/')({
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { t } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });
	const amount = usePersistedStore((state) => state.amount);
	const setAmount = usePersistedStore((state) => state.setAmount);
	const campaign = usePortalCampaignQuery(campaignId);

	const langCode = localeToLanguage[locale];

	const donationAmounts =
		!isNotFound(campaign) && campaign.data.donationAmounts.length > 0 ? campaign.data.donationAmounts : [5, 10, 25, 50];
	const donationEquivalents = !isNotFound(campaign) ? campaign.data.donationEquivalents : [];

	const possibleAmounts = donationAmounts.map((a) => a.toString());
	const [isCustomInput, setIsCustomInput] = useState(
		!possibleAmounts.includes(amount?.toString() ?? possibleAmounts[1] ?? '10'),
	);

	const form = useAppForm({
		validators: {
			onChange: z.object({
				amount: z
					.string()
					.check(z.minLength(1, t(errorMessages.amount)))
					.check((ctx) => {
						if (ctx.issues.length === 0 && (Number(ctx.value) <= 0 || Number(ctx.value) >= 3000)) {
							ctx.issues.push({ code: 'custom', message: t(errorMessages.amount), input: ctx.value });
						}
					}),
			}),
		},
		defaultValues: { amount: amount?.toString() ?? possibleAmounts[1] ?? '10' },
		onSubmit: ({ value }) => {
			setAmount(value.amount ? Number(value.amount) : undefined);
			navigate({ to: `/$portalId/$campaignId/donate/details`, params: { portalId, campaignId } });
		},
	});

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex h-full grow flex-col gap-6"
			>
				<div className="flex flex-col gap-3">
					<Title size="lg" className="text-center">
						{t`Make your donation`}
					</Title>
					<Paragraph className="text-center" as="div">
						<CampaignText />
					</Paragraph>
				</div>

				<form.AppField
					name="amount"
					children={(field) => {
						const isPreset = !isCustomInput && possibleAmounts.includes(field.state.value);
						const selectedIndex = isPreset ? donationAmounts.findIndex((a) => a.toString() === field.state.value) : -1;
						const equivalentText = selectedIndex >= 0 ? donationEquivalents[selectedIndex]?.[langCode] : undefined;

						return (
							<>
								{equivalentText && (
									<div className="flex items-center justify-center gap-2 rounded-md bg-[#f5f5f5] p-2">
										<HeartIcon className="text-primary size-6 shrink-0" />
										<p className="text-sm tracking-tight">{equivalentText}</p>
									</div>
								)}

								<RadioGroup
									onValueChange={(value) => {
										setIsCustomInput(false);
										field.handleChange(value);
									}}
									value={isPreset ? field.state.value : ''}
									className="grid grid-cols-2 gap-2"
								>
									{donationAmounts.map((da) => {
										const val = da.toString();
										const isSelected = isPreset && field.state.value === val;
										return (
											<div key={val} className="flex">
												<RadioGroupItem value={val} id={`amount-${val}`} className="peer sr-only absolute" />
												<Label
													htmlFor={`amount-${val}`}
													onClick={() => {
														if (isCustomInput && field.state.value === val) {
															setIsCustomInput(false);
														}
													}}
													className={cn(
														buttonVariants({
															variant: 'outline-dark',
															size: 'xl',
														}),
														'w-full',
														isSelected &&
															'bg-primary border-primary hover:bg-primary/70  hover:border-primary/70 text-white',
													)}
												>
													{da} €
												</Label>
											</div>
										);
									})}
								</RadioGroup>

								<div className="grow">
									<CurrencyInput
										className={cn(
											buttonVariants({
												variant: field.state.value === '' || isPreset ? 'outline-dark' : 'default',
											}),
											'placeholder:text-foreground h-[58px] w-full text-center text-[20px] focus:placeholder:text-transparent',
										)}
										allowNegativeValue={false}
										placeholder={t`Other amount`}
										decimalsLimit={2}
										decimalSeparator={'.'}
										groupSeparator={','}
										suffix={'€'}
										value={isPreset ? undefined : field.state.value}
										onValueChange={(value) => {
											setIsCustomInput(true);
											field.setValue(value ?? '');
										}}
									/>
									<FieldInfo fieldState={field.state} />
								</div>
							</>
						);
					}}
				/>

				<form.AppForm>
					<form.SubmitButton size="2xl" className="mt-6 w-full" label={t`Next`} />
				</form.AppForm>
			</form>
		</Animate>
	);
}

function CampaignText() {
	const { campaignId } = Route.useParams();
	const campaign = usePortalCampaignQuery(campaignId);

	if (isNotFound(campaign)) return null;

	return (
		<>
			{campaign.data.texts.objectives && <div dangerouslySetInnerHTML={{ __html: campaign.data.texts.objectives }} />}
		</>
	);
}
